/**
 * Backup Prompt Component
 * Shows periodic reminders to backup trip data
 * Downloads JSON file to user's device
 *
 * @version 1.0.0
 * @date 2025-11-13
 */

import React, { useState, useEffect } from 'react';
import autoBackupManager from '../utils/autoBackupManager';

const BackupPrompt = () => {
  const [show, setShow] = useState(false);
  const [daysSince, setDaysSince] = useState(0);
  const [backing, setBacking] = useState(false);
  const [lastBackupDate, setLastBackupDate] = useState(null);

  useEffect(() => {
    checkBackupStatus();
  }, []);

  const checkBackupStatus = () => {
    const status = autoBackupManager.getBackupStatus();

    if (status.needed) {
      setDaysSince(status.daysSinceLastBackup);
      setLastBackupDate(status.lastBackupDate);
      setShow(true);
    }
  };

  const handleBackup = async () => {
    setBacking(true);

    try {
      const backup = await autoBackupManager.createBackup();
      const filename = await autoBackupManager.downloadBackup(backup);

      alert(
        `✅ Backup descargado: ${filename}\n\n` +
        `Total de viajes: ${backup.stats.totalTrips}\n` +
        `Fotos: ${backup.stats.totalPhotos}\n` +
        `Notas: ${backup.stats.totalNotes}\n\n` +
        `Guárdalo en un lugar seguro (Drive, Dropbox, etc.)`
      );

      setShow(false);
    } catch (error) {
      console.error('Backup error:', error);
      alert(`❌ Error al crear backup: ${error.message}`);
    } finally {
      setBacking(false);
    }
  };

  const handleLater = () => {
    autoBackupManager.postponeBackup(3); // Remind in 3 days
    setShow(false);
  };

  const handleNever = () => {
    const confirmed = confirm(
      '¿Estás seguro de desactivar los recordatorios de backup?\n\n' +
      'ADVERTENCIA: Tus datos solo están en este dispositivo. ' +
      'Si borras la caché del navegador, los perderás.\n\n' +
      'Podrás hacer backups manualmente desde Configuración.'
    );

    if (confirmed) {
      autoBackupManager.disableBackups();
      setShow(false);
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '1rem',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '2rem',
        maxWidth: '550px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        position: 'relative'
      }}>
        {/* Icon */}
        <div style={{
          fontSize: '4rem',
          textAlign: 'center',
          marginBottom: '1rem',
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          💾
        </div>

        {/* Title */}
        <h2 style={{
          color: '#8B7355',
          marginBottom: '1rem',
          textAlign: 'center',
          fontSize: '1.8rem',
          fontWeight: '900'
        }}>
          Backup Recomendado
        </h2>

        {/* Days since last backup */}
        <p style={{
          color: '#666',
          marginBottom: '1.5rem',
          lineHeight: '1.6',
          textAlign: 'center'
        }}>
          {lastBackupDate ? (
            <>
              Último backup: <strong>{lastBackupDate}</strong>
              <br />
              Han pasado <strong style={{ color: '#e74c3c' }}>{daysSince} días</strong>
            </>
          ) : (
            <>
              <strong style={{ color: '#e74c3c' }}>Aún no has hecho ningún backup</strong>
            </>
          )}
        </p>

        {/* Warning box */}
        <div style={{
          background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          borderLeft: '4px solid #ff9800'
        }}>
          <div style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>
            <strong>⚠️ Importante:</strong>
          </div>
          <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: '1.5', margin: 0 }}>
            Los datos de tus viajes están <strong>solo en este dispositivo</strong>.
            Si borras la caché del navegador, cambias de dispositivo, o reinstales la app,
            <strong> los perderás para siempre</strong>.
          </p>
        </div>

        {/* Info box */}
        <div style={{
          background: '#f5f1eb',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          borderLeft: '4px solid #B8A082'
        }}>
          <div style={{ marginBottom: '0.5rem', fontSize: '1rem', color: '#8B7355' }}>
            <strong>📥 ¿Qué es un backup?</strong>
          </div>
          <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: '1.5', margin: 0 }}>
            Se descargará un archivo JSON con todos tus viajes, fotos, notas y presupuestos.
            Guárdalo en tu nube personal (Drive, Dropbox, iCloud) o envíalo por email.
            Si pierdes tus datos, podrás restaurarlos desde ese archivo.
          </p>
        </div>

        {/* Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <button
            onClick={handleBackup}
            disabled={backing}
            style={{
              background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
              color: 'white',
              border: 'none',
              padding: '1.2rem',
              borderRadius: '12px',
              fontSize: '1.1rem',
              fontWeight: '700',
              cursor: backing ? 'not-allowed' : 'pointer',
              opacity: backing ? 0.6 : 1,
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(39, 174, 96, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => {
              if (!backing) e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {backing ? (
              <>
                <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                Creando backup...
              </>
            ) : (
              <>
                <span>📥</span>
                Descargar Backup Ahora
              </>
            )}
          </button>

          <button
            onClick={handleLater}
            style={{
              background: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
              color: 'white',
              border: 'none',
              padding: '1rem',
              borderRadius: '12px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
            }}
          >
            🕐 Recordar en 3 días
          </button>

          <button
            onClick={handleNever}
            style={{
              background: 'transparent',
              color: '#999',
              border: 'none',
              padding: '0.8rem',
              fontSize: '0.85rem',
              cursor: 'pointer',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = '#e74c3c';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#999';
            }}
          >
            No volver a mostrar
          </button>
        </div>

        <style>
          {`
            @keyframes pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.1); }
            }

            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default BackupPrompt;
